import React from 'react'
import { camelCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Callout,
  CopyButton,
  Highlight,
  PagingSidePanelType,
  SidePanel,
  Tabs,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { useCustomerApplicationQuery } from 'hooks/queries/Customers/useCustomerApplicationQuery'
import { useGetCustomerDocumentsV2 } from 'hooks/queries/Customers/useCustomerDocumentsV2Query'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { CTASection } from 'pages/EmbeddedCustomers/CustomerDetails/CTASection'
import { EMBEDDED_CUSTOMER_STATUSES } from 'pages/EmbeddedCustomers/helpers/constants'
import type { EmbeddedCustomerData } from 'pages/EmbeddedCustomers/helpers/types'
import { Application, CustomerDocumentStatus, CustomerEntityType } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import {
  DOCUMENT_TYPES,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS,
  REQUIRED_ACTION_CODE_DOCUMENT_REQUIRED,
  REQUIRED_ACTION_CODE_QUESTIONNAIRE_REQUIRED,
} from '../helpers/constants'
import { statusHighlightTypeMap, statusLabelMap } from '../utils/utils'
import { CustomerAssociatesTab } from './CustomerAssociatesTab'
import { CustomerDocumentsTab } from './CustomerDocumentsTab'
import { CustomerInfoRequired } from './CustomerInfoRequired'
import { CustomerSidePanelBody } from './CustomerSidePanelBody'

export interface ApplicationSidePanelProps {
  isOpen: boolean
  application: Application | undefined
  paging: PagingSidePanelType
  onClose: () => void
}

export const CustomerSidePanel = ({
  isOpen,
  application,
  paging,
  onClose,
}: ApplicationSidePanelProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const onboardingLink = application?.authenticatedLink?.link

  const { reference, id, status, type } = application || {}
  const isV1CustomerOnboarding = !onboardingLink

  const { data, isLoading, isError, refetch } = useCustomerApplicationQuery(
    isV1CustomerOnboarding ? '' : application?.id || ''
  )

  const { data: v1CustomerData } = useCustomerQuery<EmbeddedCustomerData>(
    isV1CustomerOnboarding ? (id ?? '') : ''
  )

  const sumsubLink = v1CustomerData?.verification?.url
  const v1Status = v1CustomerData?.status
  const isInfoRequired = v1Status === EMBEDDED_CUSTOMER_STATUSES.INFO_REQUIRED
  const showAgreementCTA =
    !!v1CustomerData?.pendingAgreementSessionUrl &&
    v1Status === EMBEDDED_CUSTOMER_STATUSES.PENDING_AGREEMENTS
  const showVerificationCTA =
    !!sumsubLink && (isInfoRequired || v1Status === EMBEDDED_CUSTOMER_STATUSES.PENDING)
  const verificationHelpTextKey = isInfoRequired
    ? 'embeddedCustomers.details.verificationCtaPending'
    : 'embeddedCustomers.details.verificationCtaComplete'
  const isCompany = type === CustomerEntityType.COMPANY
  const companyName = data?.company?.name || application?.name || reference || id

  const pendingDocumentCodes = new Set(
    data?.requiredActions
      ?.filter(action => action?.category === REQUIRED_ACTION_CATEGORY_DOCUMENT_REQUIREMENTS)
      .map(action =>
        action?.code === REQUIRED_ACTION_CODE_DOCUMENT_REQUIRED
          ? camelCase(action?.description)
          : camelCase(action?.code)
      ) ?? []
  )
  const hasOutstandingDocuments = DOCUMENT_TYPES.some(docKey => pendingDocumentCodes.has(docKey))

  const requiresAssociateData =
    data?.requiredActions?.some(
      action => action?.category === REQUIRED_ACTION_CATEGORY_ASSOCIATES
    ) ?? false

  const { data: documentsData } = useGetCustomerDocumentsV2(id ?? '')
  const hasUploadedDocuments =
    documentsData?.content?.some(doc =>
      isV1CustomerOnboarding
        ? doc?.status === CustomerDocumentStatus.COMPLETE ||
          doc?.status === CustomerDocumentStatus.IN_PROGRESS
        : doc?.status === CustomerDocumentStatus.COMPLETE
    ) ?? false
  const showDocumentsTab = hasOutstandingDocuments || hasUploadedDocuments

  const hasQuestionnaireRequired =
    data?.requiredActions?.some(
      action => action?.code === REQUIRED_ACTION_CODE_QUESTIONNAIRE_REQUIRED
    ) ?? false

  return (
    <SidePanel
      id="side-panel-application"
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={application?.id}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <Box flexCol gap={16}>
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: [],
              primaryLabel: <h2 className="text-foreground-prominent text-h2">{companyName}</h2>,
              secondaryLabel: status && (
                <Highlight type={statusHighlightTypeMap[status]}>
                  {statusLabelMap[status] ?? status}
                </Highlight>
              ),
            }}
          />
          {onboardingLink && (
            <SidePanelSection>
              <Callout
                GhostButton={
                  <CopyButton copyText={onboardingLink} testId="copy-onboarding-link">
                    {t('customers.onboardingLink')}
                  </CopyButton>
                }
              >
                {t('customers.onboardingLinkHelp')}
              </Callout>
            </SidePanelSection>
          )}
          {showAgreementCTA && (
            <CTASection
              ctaLink={v1CustomerData.pendingAgreementSessionUrl as string}
              ctaLabel={t('embeddedCustomers.details.agreementsCtaLabel')}
              helpText={t('embeddedCustomers.details.agreementsCtaPending')}
              isPendingStatus
              isCopyAction
            />
          )}
          {showVerificationCTA && (
            <CTASection
              heading={t('embeddedCustomers.details.verificationLink')}
              ctaLink={sumsubLink as string}
              ctaLabel={t('embeddedCustomers.details.verificationCtaLabel')}
              helpText={t(verificationHelpTextKey)}
              isPendingStatus={isInfoRequired}
            />
          )}
        </Box>
      }
      body={
        <Box tag="section" width="full" background="standard">
          <Tabs
            key={application?.id}
            tabs={[
              {
                id: 'overview',
                header: t('embeddedCustomers.tabs.overview'),
                content: (
                  <CustomerSidePanelBody
                    application={application}
                    data={data}
                    isLoading={isLoading}
                    isError={isError}
                    refetch={refetch}
                  />
                ),
              },
              ...(data && showDocumentsTab
                ? [
                    {
                      id: 'documents',
                      header: t('embeddedCustomers.tabs.documents'),
                      content: (
                        <CustomerDocumentsTab
                          customerReference={id ?? ''}
                          pendingDocumentCodes={pendingDocumentCodes}
                          isV1CustomerOnboarding={isV1CustomerOnboarding}
                        />
                      ),
                      badgeCount: hasOutstandingDocuments ? '!' : undefined,
                    },
                  ]
                : []),
              ...(data &&
              isCompany &&
              (!!data?.company?.associates?.length || requiresAssociateData)
                ? [
                    {
                      id: 'associates',
                      header: t('embeddedCustomers.tabs.associates'),
                      content: (
                        <CustomerAssociatesTab
                          associates={data}
                          isV1CustomerOnboarding={isV1CustomerOnboarding}
                          customerReference={id ?? ''}
                        />
                      ),
                      badgeCount: requiresAssociateData ? '!' : undefined,
                    },
                  ]
                : []),
              ...(hasQuestionnaireRequired
                ? [
                    {
                      id: 'actions-required',
                      header: t('embeddedCustomers.tabs.actionsRequired'),
                      content: (
                        <CustomerInfoRequired
                          data={data}
                          isLoading={isLoading}
                          isError={isError}
                          refetch={refetch}
                        />
                      ),
                      badgeCount: '!',
                    },
                  ]
                : []),
            ]}
            defaultTabId="overview"
          />
        </Box>
      }
    />
  )
}
